import { useSearchParams } from 'react-router-dom';

/** Synthetic category code used to represent the default "Trending" view. */
export const TRENDING_CATEGORY_CODE = 'TRENDING';

/** Synthetic id used by the leading "All" sub-category tab. */
export const ALL_SUB_CATEGORY_ID = 'ALL';

/** Options accepted by the search-param setters returned by {@link useAppSearchParams}. */
export interface SetSearchParamOptions {
  /** Replace the current history entry instead of pushing a new one. */
  replace?: boolean;
}

/**
 * Shape returned by {@link useAppSearchParams}.
 */
export interface UseAppSearchParamsResult {
  /** Underlying `URLSearchParams` from `react-router-dom` for ad-hoc reads. */
  searchParams: URLSearchParams;
  /** Underlying setter from `react-router-dom`. Prefer the typed setters below. */
  setSearchParams: ReturnType<typeof useSearchParams>[1];
  /** Raw `?category=` value, or `null` when absent. */
  category: string | null;
  /** Raw `?subCategory=` value, or `null` when absent. */
  subCategory: string | null;
  /** Active category code, defaulting to {@link TRENDING_CATEGORY_CODE} when absent. */
  categoryCode: string;
  /** True when no category and no sub-category are selected. */
  isTrending: boolean;
  /** True when a sub-category is selected (drill-down / flat list view). */
  isFlat: boolean;
  /**
   * Update the `category` search param. Passing `null`, `''`, or
   * {@link TRENDING_CATEGORY_CODE} removes the param. Always clears
   * `subCategory` so the new category starts on its "All" tab.
   */
  setCategory: (code: string | null, opts?: SetSearchParamOptions) => void;
  /**
   * Update the `subCategory` search param. Passing `null`, `''`, or
   * {@link ALL_SUB_CATEGORY_ID} removes the param.
   */
  setSubCategory: (code: string | null, opts?: SetSearchParamOptions) => void;
}

/**
 * Centralized read/write access to the lobby URL contract
 * (`?category=` and `?subCategory=`).
 *
 * Single source of truth for:
 * - defaulting `category` to {@link TRENDING_CATEGORY_CODE}
 * - clearing `subCategory` when the category changes
 * - deleting params for synthetic "all"/"trending" sentinels
 *
 * Use this hook instead of calling `useSearchParams` directly so the URL
 * contract stays consistent across the app.
 */
export function useAppSearchParams(): UseAppSearchParamsResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category');
  const subCategory = searchParams.get('subCategory');

  const setCategory = (code: string | null, opts?: SetSearchParamOptions) => {
    const next = new URLSearchParams(searchParams);
    if (!code || code === TRENDING_CATEGORY_CODE) {
      next.delete('category');
    } else {
      next.set('category', code);
    }
    next.delete('subCategory');
    setSearchParams(next, opts);
  };

  const setSubCategory = (code: string | null, opts?: SetSearchParamOptions) => {
    const next = new URLSearchParams(searchParams);
    if (!code || code === ALL_SUB_CATEGORY_ID) {
      next.delete('subCategory');
    } else {
      next.set('subCategory', code);
    }
    setSearchParams(next, opts);
  };

  return {
    searchParams,
    setSearchParams,
    category,
    subCategory,
    categoryCode: category ?? TRENDING_CATEGORY_CODE,
    isTrending: !category && !subCategory,
    isFlat: Boolean(subCategory),
    setCategory,
    setSubCategory,
  };
}
